import type { paths } from '@forgd/openapi/api'
import type { EntryKey, UseQueryOptions } from '@pinia/colada'
import type {
  SuccessResponseBody,
  ValidMethods,
} from '../types/api'

import type { QueryConfig } from './useApiFetch'
import { useQuery, useQueryCache } from '@pinia/colada'
import { deepUnref } from '../utils/vue'
import { apiFetch, parseApiFetchParams } from './useApiFetch'

/**
 * Get the cache key for a given path and config
 *
 *  @example
 *  const cacheKey = getApiQueryCacheKey('/projects/{projectId}', {
 *    params: () => ({
 *      path: {
 *        projectId: '123',
 *      },
 *    }),
 *  })
 */
export function getApiQueryCacheKey<P extends keyof paths, M extends ValidMethods<P>>(path: P, config?: QueryConfig<P, M>): EntryKey {
  // allow custom cache keys
  if (config?.key) {
    const resolvedKey = typeof config.key === 'function' ? config.key() : config.key
    return [path, ...(Array.isArray(resolvedKey) ? resolvedKey : [resolvedKey])]
  }
  const params = config?.params ? parseApiFetchParams(config.params) : undefined
  if (!config || !params) {
    return [path]
  }

  return [path, ...(params.path ? [params.path] : []), ...(params.query ? [params.query] : [])]
}

export function invalidateQueryByUrl<P extends keyof paths>(url: P) {
  const queryCache = useQueryCache()
  const entries = queryCache.getEntries().filter(entry => entry.key[0] === url)

  return Promise.all(entries.map(entry => queryCache.invalidateQueries({ key: entry.key })))
}

/**
 * Build a query options object that can be used with the useQuery hook from pinia colada
 *
 *  @example
 *  const projectId = ref('')
 *  const { data } = useQuery({
 *    ...buildApiQuery('/projects/{projectId}', {
 *      params: () => ({
 *        path: {
 *          projectId: projectId.value,
 *        },
 *      }),
 *    }),
 *    enabled: () => !!projectId.value,
 *  })
 */
export function buildApiQuery<P extends keyof paths, M extends ValidMethods<P>>(
  path: P,
  config?: QueryConfig<P, M>,
): UseQueryOptions<SuccessResponseBody<P, M>, Error> {
  return {
    key: () => getApiQueryCacheKey<P, M>(path, config),
    query: async () => {
      const params = config?.params ? parseApiFetchParams(config.params) : undefined
      const parsedConfig = config ? deepUnref(config) : undefined
      return apiFetch<P, M>(path, { ...parsedConfig, params: params as QueryConfig<P, M>['params'] })
    },
    placeholderData: previousData => toValue(config?.options)?.stableData ? previousData : undefined,
  }
}

/**
 * SSR-safe version of buildApiQuery that catches errors during SSR to prevent page crashes.
 *
 * Due to a known issue with Pinia Colada (https://github.com/posva/pinia-colada/issues/229),
 * errors during SSR can crash the page render. This version catches errors during SSR
 * and returns null instead of throwing.
 *
 * Components using this should check for both `query.error.value` and `!query.data.value`
 * to display appropriate error/empty states. Use with PrQueryState component:
 * `<PrQueryState :is-loading="query.isLoading.value" :has-data="!!query.data.value">`
 *
 *  @example
 *  const { data, error } = useQuery({
 *    ...buildApiQuerySSRSafe('/public-research/some-endpoint', {
 *      params: () => ({ query: { ... } }),
 *    }),
 *  })
 */
export function buildApiQuerySSRSafe<P extends keyof paths, M extends ValidMethods<P>>(
  path: P,
  config?: QueryConfig<P, M>,
): UseQueryOptions<SuccessResponseBody<P, M> | null, Error> {
  return {
    key: () => getApiQueryCacheKey<P, M>(path, config),
    query: async () => {
      const params = config?.params ? parseApiFetchParams(config.params) : undefined
      const parsedConfig = config ? deepUnref(config) : undefined
      try {
        return await apiFetch<P, M>(path, { ...parsedConfig, params: params as QueryConfig<P, M>['params'] })
      }
      catch (error) {
        // During SSR, don't throw errors as they crash the page render
        // Instead, return null and let components handle the missing data state
        // @see https://github.com/posva/pinia-colada/issues/229
        if (import.meta.server) {
          // Log only safe metadata to prevent leaking sensitive information
          const errorInfo = error instanceof Error
            ? { name: error.name, status: (error as any).status || (error as any).statusCode }
            : { type: typeof error }
          console.error(`[SSR] API Error for ${path}:`, errorInfo)
          return null
        }
        // On client, throw normally so Pinia Colada can set query.error
        throw error
      }
    },
    placeholderData: previousData => toValue(config?.options)?.stableData ? previousData : undefined,
  }
}

/**
 * Wrapper for using Pinia Colada Query.
 *
 * Consider using buildApiQuery if you need more granular control over how Pinia is behaving
 * although the preference is adding semantic options to handle Forgd use cases.
 */
export function usePiniaColadaQuery<P extends keyof paths, M extends ValidMethods<P>>(
  path: P,
  config?: QueryConfig<P, M>,
) {
  return useQuery<SuccessResponseBody<P, M>, Error>(buildApiQuery<P, M>(path, config))
}

/**
 * Define a query options object that can be used with the useQuery hook from pinia colada,
 * This is a convenience function that will build the query options object for you
 *
 *  @example
 *  const useProjectByIdQuery = defineApiQuery('/projects/{projectId}')
 *  const projectId = ref('')
 *  const { data } = useQuery({
 *    ...useProjectByIdQuery({
 *      params: () => ({
 *        path: {
 *          projectId: projectId.value,
 *        },
 *      }),
 *    }),
 *    enabled: () => !!projectId.value,
 *  })
 */
export function defineApiQuery<P extends keyof paths, M extends ValidMethods<P>>(path: P) {
  // Return a function that takes the optional config and
  // calls the low-level buildQuery for you
  return (config?: QueryConfig<P, M>) =>
    buildApiQuery<P, M>(path, config)
}
